import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Button, Label } from 'reactstrap';

import globalTranslations from '../../../../../i18n/globalTranslations';
import OrderCheckoutProduct from '../../../../../models/order/checkout/OrderCheckoutProduct';
import { modelOf } from '../../../../../prop-types';
import CheckoutStore from '../../../../../store/CheckoutStore';
import RouteService from '../../../../../services/RouteService';
import Icon from '../../../../common/Icon';
import ProductAvailabilityText from '../../../../product/ProductAvailabilityText';
import ProductClass from '../../../../../types/ProductClass';

const CheckoutCartProductDetails = ({
  checkoutStore,
  intl,
  routeService,
  onCopyProductCode,
  product,
  productCodeCopied = false,
  showProductCode = false,
  unitPriceContent,
  hideNameAndUnitPrice = false,
  onlyNameAndUnitPrice = false,
}) => {
  const isB2B = checkoutStore.order?.isB2BCheckout ?? false;
  const productCodeVariant = isB2B ? 'copyable' : 'none';
  const variationsInline = isB2B;

  const canonicalUrl = routeService.getCanonicalProductPath(product);
  const nameLabel = (
    <Label className="CheckoutCartProductDetails__name">{product.name}</Label>
  );

  const renderProductCode = () => {
    if (!showProductCode || !product.actual_code) return null;
    if (productCodeVariant === 'copyable') {
      return (
        <div className="CheckoutCartProductDetails__code">
          <span className="CheckoutCartProductDetails__code-label">
            <FormattedMessage {...globalTranslations.productCodeTitle} />:
          </span>
          <span className="CheckoutCartProductDetails__code-value">
            {product.actual_code}
          </span>
          <Button
            color="link"
            className={
              productCodeCopied
                ? 'CheckoutCartProductDetails__code-copy CheckoutCartProductDetails__code-copy--copied'
                : 'CheckoutCartProductDetails__code-copy'
            }
            onClick={onCopyProductCode}
            aria-label={intl.formatMessage(
              globalTranslations.copyProductCodeTitle
            )}
          >
            <Icon name={productCodeCopied ? 'check' : 'copy'} />
          </Button>
        </div>
      );
    }
    if (productCodeVariant === 'simple') {
      return (
        <div className="CheckoutCartProductDetails__code">
          {product.actual_code}
        </div>
      );
    }
    return null;
  };

  const titleContent = (
    <div className="CheckoutCartProductDetails__title">
      {canonicalUrl ? <Link to={canonicalUrl}>{nameLabel}</Link> : nameLabel}
      {unitPriceContent}
    </div>
  );

  if (onlyNameAndUnitPrice) {
    return (
      <div className="CheckoutCartProductDetails__info">{titleContent}</div>
    );
  }

  return (
    <div className="CheckoutCartProductDetails__info">
      {!hideNameAndUnitPrice && titleContent}
      {product.variations && product.variations.length > 0 && (
        <div
          className={classNames(
            variationsInline
              ? 'CheckoutCartProductDetails__variations CheckoutCartProductDetails__variations--inline'
              : 'CheckoutCartProductDetails__variations',
            {
              'CheckoutCartProductDetails__variations--structure-indent':
                product.class === ProductClass.STRUCTURE,
            }
          )}
        >
          {variationsInline ? (
            <span>{product.variations.join(' · ')}</span>
          ) : (
            <ul>
              {product.variations.map((variation, index) => (
                <li key={index}>{variation}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {renderProductCode()}
      <div className="CheckoutCartProductDetails__availability">
        <p>
          <ProductAvailabilityText
            availabilityHtml={product.availability_html}
          />
        </p>
      </div>
      {product.not_valid_for_campaign_code && (
        <div className="CheckoutCartProductDetails__discount-info">
          <p>
            <FormattedMessage
              id="checkout.cart.productNotEligibleForCampaign"
              defaultMessage="No discount for this product with the given code"
            />
          </p>
        </div>
      )}
    </div>
  );
};

CheckoutCartProductDetails.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  onCopyProductCode: PropTypes.func,
  product: modelOf(OrderCheckoutProduct).isRequired,
  productCodeCopied: PropTypes.bool,
  showProductCode: PropTypes.bool,
  unitPriceContent: PropTypes.node,
  hideNameAndUnitPrice: PropTypes.bool,
  onlyNameAndUnitPrice: PropTypes.bool,
};

export default injectIntl(
  inject('checkoutStore', 'routeService')(observer(CheckoutCartProductDetails))
);
